import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "./Input";
import { Button } from "./Button";

export type BaitSelection = {
  canonical_id: string | null;
  user_bait_id: number | null;
  snapshot: string;
};

const EMPTY: BaitSelection = {
  canonical_id: null,
  user_bait_id: null,
  snapshot: "",
};

function labelFor(b: any) {
  if (b.brand && b.brand !== "Generic") return `${b.brand} ${b.product_name}`;
  return b.product_name ?? b.display_name ?? "";
}

function defaultBaitType(role?: string, query?: string) {
  const q = String(query ?? "").toLowerCase();
  if (q.includes("zig") || q.includes("foam")) return "zig_foam";
  if (role === "hookbait") return "hookbait";
  if (role === "additive") return "liquid";
  return "boilie";
}

function baitSearchTokens(value: string) {
  return Array.from(new Set(value.toLowerCase().match(/[a-z0-9]+/g) ?? []))
    .filter((token) => token.length >= 2)
    .slice(0, 6);
}

function baitSearchText(bait: any) {
  return [bait?.brand, bait?.range_name, bait?.product_name, bait?.display_name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Compact inline bait picker: canonical + personal bait, with use-now/review-later additions. */
export function BaitPicker({
  id,
  label,
  value,
  onChange,
  speciesFilter,
  presentationRole,
  placeholder,
}: {
  id: string;
  label: string;
  value: BaitSelection;
  onChange: (v: BaitSelection) => void;
  speciesFilter?: string;
  presentationRole?: string;
  placeholder?: string;
}) {
  const externalSnapshot =
    value.canonical_id || value.user_bait_id ? value.snapshot || "" : null;
  const [query, setQuery] = useState(value.snapshot || "");
  const [open, setOpen] = useState(false);
  const [showHomemade, setShowHomemade] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [homemadeName, setHomemadeName] = useState("");
  const [homemadeCategory, setHomemadeCategory] = useState("");
  const [addBrand, setAddBrand] = useState("");
  const [addProduct, setAddProduct] = useState("");
  const [addType, setAddType] = useState(defaultBaitType(presentationRole, value.snapshot));
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value.snapshot || "");
  }, [value.snapshot]);

  const displayQuery = externalSnapshot !== null ? externalSnapshot : query;
  const tokens = baitSearchTokens(query.trim());
  const searchEnabled = tokens.length > 0;

  const { data: results = [] } = useQuery({
    queryKey: ["bait_search", query, speciesFilter, presentationRole],
    queryFn: async () => {
      const tokenResults = await Promise.all(
        tokens.map(async (token) => {
          const params = new URLSearchParams({ q: token });
          if (speciesFilter) params.set("species", speciesFilter);
          if (presentationRole) params.set("role", presentationRole);
          const response = await fetch(`/app-api/baits/search?${params}`);
          if (!response.ok) return [];
          const rows = await response.json();
          return Array.isArray(rows) ? rows : [];
        }),
      );

      const candidates = new Map<string, any>();
      const tokenHits = new Map<string, Set<string>>();

      tokenResults.forEach((rows, index) => {
        const token = tokens[index];
        for (const bait of rows) {
          const key = String(bait.id ?? `${bait.brand ?? ""}|${bait.product_name ?? ""}`);
          if (!candidates.has(key)) candidates.set(key, bait);
          if (!tokenHits.has(key)) tokenHits.set(key, new Set());
          tokenHits.get(key)?.add(token);
        }
      });

      return Array.from(candidates.entries())
        .filter(([key, bait]) => {
          const searchable = baitSearchText(bait);
          return tokens.every(
            (token) => tokenHits.get(key)?.has(token) || searchable.includes(token),
          );
        })
        .map(([, bait]) => bait)
        .slice(0, 30);
    },
    enabled: searchEnabled && open,
    staleTime: 30_000,
  });

  const { data: userBaits = [], refetch: refetchUserBaits } = useQuery({
    queryKey: ["carpintel_user_baits"],
    queryFn: () => fetch("/app-api/baits/mine").then((r) => r.json()),
  });

  function select(b: any) {
    const snap = b.display_name ? b.display_name : labelFor(b);
    onChange({
      canonical_id: b.id?.startsWith?.("bait_") ? b.id : null,
      user_bait_id: b.user_bait_id ?? null,
      snapshot: snap,
    });
    setQuery(snap);
    setOpen(false);
  }

  function clearSelection() {
    onChange({ ...EMPTY, snapshot: query });
  }

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    onChange({ canonical_id: null, user_bait_id: null, snapshot: v });
    setOpen(true);
    setShowHomemade(false);
    setShowAdd(false);
    setSubmitMsg("");
  }

  async function saveHomemade() {
    if (!homemadeName.trim()) return;
    setSubmitting(true);
    setSubmitMsg("");
    try {
      const res = await fetch("/app-api/baits/mine", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name: homemadeName,
          bait_category: homemadeCategory,
        }),
      });
      const bait = await res.json();
      if (!res.ok) throw new Error(bait.error || "Could not save bait");
      onChange({ canonical_id: null, user_bait_id: bait.id, snapshot: bait.display_name });
      setQuery(bait.display_name);
      setShowHomemade(false);
      setHomemadeName("");
      setOpen(false);
      await refetchUserBaits();
      setSubmitMsg("Private bait saved and selected.");
    } catch (error) {
      setSubmitMsg(error instanceof Error ? error.message : "Could not save. Try again.");
    }
    setSubmitting(false);
  }

  async function addMissingBait() {
    if (!addProduct.trim()) return;
    setSubmitting(true);
    setSubmitMsg("");
    try {
      const response = await fetch("/app-api/baits/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brand: addBrand,
          product_name: addProduct,
          bait_type: addType,
          presentation_role: presentationRole || "both",
          species_applicability:
            speciesFilter === "carp" || speciesFilter === "catfish"
              ? speciesFilter
              : "both",
        }),
      });
      const bait = await response.json();
      if (!response.ok || !bait.id)
        throw new Error(bait.error || "Could not add bait");
      onChange({
        canonical_id: null,
        user_bait_id: Number(bait.id),
        snapshot: bait.display_name,
      });
      setQuery(bait.display_name);
      setShowAdd(false);
      setOpen(false);
      setAddBrand("");
      setAddProduct("");
      setAddType(defaultBaitType(presentationRole, ""));
      await refetchUserBaits();
      setSubmitMsg("Added and selected. CatchCompass Admin will review it for the shared bait list.");
    } catch (error) {
      setSubmitMsg(error instanceof Error ? error.message : "Could not add bait. Try again.");
    }
    setSubmitting(false);
  }

  const matchedUserBaits = userBaits.filter((b: any) => {
    const searchable = baitSearchText(b);
    if (tokens.length) return tokens.every((token) => searchable.includes(token));
    return !query || searchable.includes(query.toLowerCase());
  });
  const hasResults = results.length > 0 || matchedUserBaits.length > 0;
  const showDropdown = open && (hasResults || searchEnabled);
  const rulesCheckItem = results.find(
    (b: any) => b.rules_check_required && b.id === value.canonical_id,
  );

  return (
    <div className="relative" ref={containerRef}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-primary">
        {label}
      </label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={displayQuery}
          onChange={handleQueryChange}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder ?? "Search or type a bait name…"}
          autoComplete="off"
        />
        {(value.canonical_id || value.user_bait_id) && (
          <button
            type="button"
            onClick={clearSelection}
            className="shrink-0 text-xs text-secondary hover:text-primary"
            aria-label="Clear bait selection"
          >
            ✕
          </button>
        )}
      </div>

      {rulesCheckItem && (
        <p className="mt-1 text-xs text-warning">⚠ Check fishery rules and local regulations before using this bait.</p>
      )}
      {submitMsg && !showHomemade && !showAdd ? (
        <p className="mt-1 text-xs text-secondary" role="status">{submitMsg}</p>
      ) : null}

      {showDropdown && (
        <div className="absolute left-0 right-0 z-50 isolate mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-raised shadow-xl">
          {results.length > 0 && (
            <div>
              <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">Known baits</p>
              {results.map((b: any) => (
                <button
                  key={b.id}
                  type="button"
                  onMouseDown={() => select(b)}
                  className="flex w-full flex-col px-3 py-2.5 text-left hover:bg-inset"
                >
                  <span className="text-sm font-medium text-primary">{labelFor(b)}</span>
                  <span className="text-xs text-secondary capitalize">
                    {b.bait_type?.replace(/_/g, " ")}
                    {b.rules_check_required ? " · ⚠ check fishery rules" : ""}
                  </span>
                </button>
              ))}
            </div>
          )}

          {matchedUserBaits.length > 0 && (
            <div>
              <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary">Your added baits</p>
              {matchedUserBaits.map((b: any) => (
                <button
                  key={`user-${b.id}`}
                  type="button"
                  onMouseDown={() => select({ ...b, user_bait_id: b.id, id: null })}
                  className="flex w-full flex-col px-3 py-2.5 text-left hover:bg-inset"
                >
                  <span className="text-sm font-medium text-primary">{b.display_name}</span>
                  <span className="text-xs text-secondary">{b.bait_category || "Personal bait"}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3 border-t border-border px-3 py-2">
            <button
              type="button"
              onMouseDown={() => {
                setShowAdd(true);
                setAddProduct(query.trim());
                setAddType(defaultBaitType(presentationRole, query));
                setOpen(false);
                setSubmitMsg("");
              }}
              className="text-xs font-semibold text-accent hover:underline"
            >
              + Add missing bait
            </button>
            <button
              type="button"
              onMouseDown={() => {
                setShowHomemade(true);
                setHomemadeName(query.trim());
                setOpen(false);
                setSubmitMsg("");
              }}
              className="text-xs text-secondary hover:underline"
            >
              Keep a homemade bait private
            </button>
          </div>
        </div>
      )}

      {showHomemade && (
        <div className="mt-2 space-y-2 rounded-lg border border-border bg-raised p-3">
          <p className="text-xs font-semibold text-primary">Add private homemade bait</p>
          <Input
            placeholder="Your private bait name"
            value={homemadeName}
            onChange={(e) => setHomemadeName(e.target.value)}
          />
          <Input
            placeholder="Category (optional)"
            value={homemadeCategory}
            onChange={(e) => setHomemadeCategory(e.target.value)}
          />
          <p className="text-xs text-secondary">Private to you and not sent for Admin review.</p>
          {submitMsg && <p className="text-xs text-error">{submitMsg}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={saveHomemade} isLoading={submitting} type="button">Save and use</Button>
            <Button size="sm" variant="tertiary" onClick={() => setShowHomemade(false)} type="button">Cancel</Button>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="mt-2 space-y-2 rounded-lg border border-accent/40 bg-raised p-3">
          <p className="text-xs font-semibold text-primary">Add a missing bait</p>
          <p className="text-xs leading-5 text-secondary">
            It becomes usable by you immediately. Admin then checks it and can approve it or link it to an existing duplicate.
          </p>
          <Input
            placeholder="Brand (optional)"
            value={addBrand}
            onChange={(e) => setAddBrand(e.target.value)}
          />
          <Input
            placeholder="Bait / product name *"
            value={addProduct}
            onChange={(e) => setAddProduct(e.target.value)}
            required
          />
          <select
            value={addType}
            onChange={(e) => setAddType(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"
          >
            <option value="boilie">Boilie</option>
            <option value="pellet">Pellet</option>
            <option value="particle">Particle</option>
            <option value="pop_up">Pop-up</option>
            <option value="wafter">Wafter</option>
            <option value="hookbait">Hookbait</option>
            <option value="zig_foam">Zig foam</option>
            <option value="groundbait">Groundbait</option>
            <option value="meat">Meat</option>
            <option value="liquid">Liquid / additive</option>
            <option value="other">Other</option>
          </select>
          {submitMsg && <p className="text-xs text-error">{submitMsg}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={addMissingBait} isLoading={submitting} type="button">Add and use now</Button>
            <Button size="sm" variant="tertiary" onClick={() => setShowAdd(false)} type="button">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
